//import { useFlags } from "launchdarkly-react-client-sdk";
import loadable from '@loadable/component';
import { ConfigProvider, Layout, message, Row } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Switch, Route, useLocation, Redirect, matchPath } from 'react-router';
import { Routes, RoutePatterns, readAccessCodeFromLocation } from './helpers/routes';

import { IApplicationState } from './store';
import LoadingSpinner from './components/loading-spinner/loading-spinner';
import TicketsPage from './pages/tickets/tickets';
import ProcessingPage from './pages/processing/processing';
import GoogleTagManager from './helpers/googleTagManager';
import { actionCreators as AppSettingActions } from './store/AppSettingsState';
import LogRocket from 'logrocket';
import AppInsightsInitializer from './Logging/appInsightsInitializer';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { appInsightsReactPlugin } from "./Logging/appInsights";
import ExitIntentComponent from './components/exit-intent/exit-intent';
import { Helmet, HelmetProvider } from "react-helmet-async";
import useDocumentLocale from './hooks/useDocumentLocale';
import { languageList, replaceLanguageInPath } from './helpers/lang';
import useUrlLanguageSync from './hooks/useUrlLanguageSync';

import { actionCreators as AttributionActions } from './store/Attribution';
import { actionCreators as SiteActions } from './store/Sites';

import 'moment/locale/en-au';
import 'moment/locale/en-gb';
import 'moment/locale/ja';
import 'moment/locale/es';
import 'moment/locale/pt';
import 'moment/locale/zh-tw';
import 'moment/locale/zh-cn';
import 'moment/locale/th';
import 'moment/locale/de';
import 'moment/locale/ar-sa';
import 'moment/locale/fr';
import 'moment/locale/ca';
import 'moment/locale/fr-ca';
import 'moment/locale/nl';
import 'moment/locale/it';
import 'moment/locale/da';
import 'moment/locale/sk';
import 'moment/locale/cs';

import ja_JP from 'antd/lib/locale-provider/ja_JP';
import es_ES from 'antd/lib/locale-provider/es_ES';
import en_US from 'antd/lib/locale-provider/en_US';
import pt_PT from 'antd/lib/locale-provider/pt_PT';
import zh_TW from 'antd/lib/locale-provider/zh_TW';
import zh_CN from 'antd/lib/locale-provider/zh_CN';
import th_TH from 'antd/lib/locale-provider/th_TH';
import de_DE from 'antd/lib/locale-provider/de_DE';
// antd does not ship an ar_SA locale -- ar_EG is the closest available Arabic locale
import ar_SA from 'antd/lib/locale-provider/ar_EG';
import fr_FR from 'antd/lib/locale-provider/fr_FR';
import ca_ES from 'antd/lib/locale-provider/ca_ES';
import nl_NL from 'antd/lib/locale-provider/nl_NL';
import it_IT from 'antd/lib/locale-provider/it_IT';
import da_DK from 'antd/lib/locale-provider/da_DK';
import sk_SK from 'antd/lib/locale-provider/sk_SK';
import cs_CZ from 'antd/lib/locale-provider/cs_CZ';
import BrazeEvents from './helpers/braze';
import Cookie, { CookieNames } from './components/cookie/Cookie';
import GoogleTagManagerHeader, { addGtmContainer } from './components/GoogleTagManagerHeader/GoogleTagManagerHeader';
import { useCookies } from 'react-cookie';
import { ConfigCatProvider } from 'configcat-react';
import FeatureFlagLoader from './service/featureFlagLoader';
import ErrorBoundry from './components/error/ErrorBoundry';
import DebugPanel from './components/DebugWidget/DebugPanel';
import GlobalGiftsPage from './pages/globalGifts/globalGifts';
import RedeemLocationsPage from './pages/globalGifts/redeemLocations';

import('smoothscroll-polyfill').then((smoothscroll) => {
    smoothscroll.polyfill();
});

const { Content } = Layout;

// Maps i18n language code to Ant Design locale provider for UI component localisation
const antdLocaleMap: Record<string, typeof en_US> = {
	'ja-JP': ja_JP,
	'es-ES': es_ES,
	'pt-PT': pt_PT,
	'zh-TW': zh_TW,
	'zh-CN': zh_CN,
	'th-TH': th_TH,
	'de-DE': de_DE,
	'ar-SA': ar_SA,
	'fr-FR': fr_FR,
	'fr-CA': fr_FR, // antd does not ship fr_CA
	'ca-ES': ca_ES,
	'nl-NL': nl_NL,
	'it-IT': it_IT,
	'da-DK': da_DK,
	'sk-SK': sk_SK,
	'cs-CZ': cs_CZ,
};

const App: React.FC = () => {
    const { t } = useTranslation();
    useDocumentLocale();
    useUrlLanguageSync();
    const location = useLocation();
    const selectedLanguage = useSelector((state: IApplicationState) => state.intl.selectedLanguage);
    const currentLocale = selectedLanguage.code;
    const selectedSite = useSelector((state: IApplicationState) => state.sites.selectedSite);
    const selectedSiteGtmKey = useSelector((state: IApplicationState) => state.sites.selectedSiteGtmKey);
    const bookingFailureMessage = useSelector((state: IApplicationState) => state.booking.failedMessageKey);
    const direction = selectedLanguage.rtl ? 'rtl' : 'ltr';
    const csp = { nonce: 'zerolatencycsp' };
    const dispatch = useDispatch();
    const globalGtmEndpoint = useSelector((state: IApplicationState) => state.appSettings.gtmEndpoint);
    const logRocketId = useSelector((state: IApplicationState) => state.appSettings.logRocketId);
    const gtmSendToClients = useSelector((state: IApplicationState) => state.appSettings.gtmSendToClients);
    const brazeApiKey = useSelector((state: IApplicationState) => state.appSettings.brazeApiKey);
    const brazeEndpoint = useSelector((state: IApplicationState) => state.appSettings.brazeEndpoint);
    const newsletterGroup = useSelector((state: IApplicationState) => state.appSettings.newsletterEmailGroup);
    const updatesGroup = useSelector((state: IApplicationState) => state.appSettings.updatesEmailGroup);
    const offersGroup = useSelector((state: IApplicationState) => state.appSettings.offersEmailGroup);
    const auSmsGroup = useSelector((state: IApplicationState) => state.appSettings.auSmsGroup);
    const usSmsGroup = useSelector((state: IApplicationState) => state.appSettings.usSmsGroup);
    const caSmsGroup = useSelector((state: IApplicationState) => state.appSettings.caSmsGroup);
    const appSettingsAreLoaded = useSelector((state: IApplicationState) => state.appSettings.settingsLoaded);
    const configCatSdkKey = useSelector((state: IApplicationState) => state.appSettings.configCatSdkKey);
    const environment = useSelector((state: IApplicationState) => state.appSettings.environment);

    const [cookies] = useCookies([CookieNames.Performance, CookieNames.Functional, CookieNames.Marketing, CookieNames.IsCookieSaved]);

    // Keep the latest consent cookies in a ref so the GTM-container effect can read the
    // current snapshot without re-running on every consent change (which would re-push
    // Consent Mode 'default' and reset state — consent updates are handled in
    // GoogleTagManagerHeader instead).
    const cookiesRef = useRef(cookies);
    cookiesRef.current = cookies;

    const AddOnsComponent = loadable(() => import('./pages/addons/addons'), { fallback: <LoadingSpinner /> });
    const CheckoutComponent = loadable(() => import('./pages/checkout/checkout'), { fallback: <LoadingSpinner /> });
    const CheckoutPtvComponent = loadable(() => import('./pages/checkout-ptv/checkout-ptv'), { fallback: <LoadingSpinner /> });
    const CompleteComponent = loadable(() => import('./pages/complete/complete'), { fallback: <LoadingSpinner /> });
    const DetailsComponent = loadable(() => import('./pages/details/details'), { fallback: <LoadingSpinner /> });
    const NotFoundComponent = loadable(() => import('./pages/not-found/not-found'), { fallback: <LoadingSpinner /> });
    const SplitPaymentComponent = loadable(() => import('./pages/split-payment/split-payment'), { fallback: <LoadingSpinner /> });
    const SplitAllocateComponent = loadable(() => import('./pages/split-allocate/split-allocate'), { fallback: <LoadingSpinner /> });
    const SplitJoinComponent = loadable(() => import('./pages/split-join/split-join'), { fallback: <LoadingSpinner /> });
    const BalancePaymentComponent = loadable(() => import('./pages/balance-payment/balance-payment'), { fallback: <LoadingSpinner /> });

    const ErrorDisplayComponent = loadable(() => import('./components/error-display/error-display'), {
        fallback: <React.Fragment />,
    });

    const ReservationTimerComponent = loadable(() => import('./components/reservation-timer/reservation-timer'), {
        fallback: <React.Fragment />,
    });

    const [showDebugPanel, setShowDebugPanel] = useState(false);
    const [reservationTimerHeight, setReservationTimerHeight] = useState(0);
    
    // Memoize the height change callback to prevent unnecessary re-renders
    const handleReservationTimerHeightChange = React.useCallback((height: number) => {
        setReservationTimerHeight(height);
    }, []);
    
    useEffect(() => {
        const host = window.location.hostname;
        const isLocalhost = host.includes('localhost');
        const isDev = host.includes('dev.');
        const isUat = host.includes('uat.');

        if (environment !== 'prod' && (isLocalhost || isDev || isUat)) {
            // Expose toggle function to browser dev console only in non prod environments
            (window as any).ZL_toggleDebugPanel = () => {
                setShowDebugPanel((prev) => {
                    const newState = !prev;
                    console.log("[ZlDebugPanel] Toggled:", newState);
                    return newState;
                });
            };
            
            // Set initial state based on environment
            if (isLocalhost) {
                // For localhost: start enabled and minimized in bottom-right
                setShowDebugPanel(true);
            }
            // For dev/uat: start hidden by default, can be toggled via console
        }
    }, [environment]); // Removed showDebugPanel from dependencies to fix toggle issue


    useEffect(() => {
        // initialize the SDK
        if (!appSettingsAreLoaded) {
            console.log('skipping braze, app settings are not yet loaded');
            return;
        }

        BrazeEvents.Init(brazeApiKey, brazeEndpoint, newsletterGroup, updatesGroup, offersGroup, auSmsGroup, usSmsGroup, caSmsGroup);
    }, [appSettingsAreLoaded, brazeApiKey, brazeEndpoint, newsletterGroup, updatesGroup, offersGroup, auSmsGroup, usSmsGroup, caSmsGroup]);

    useEffect(() => {
        dispatch(AppSettingActions.loadSettings());
    }, [dispatch]);

    useEffect(() => {
        // Load in UTM Data if there is any. Rebuild params from location.search so the
        // effect has an honest, stable dependency (useQueryString returns a fresh
        // URLSearchParams every render).
        const params = new URLSearchParams(location.search);
        const utmCampaign = params.get('utm_campaign') ?? undefined;
        const utmContent = params.get('utm_content') ?? undefined;
        const utmId = params.get('utm_id') ?? undefined;
        const utmMedium = params.get('utm_medium') ?? undefined;
        const utmSource = params.get('utm_source') ?? undefined;
        const utmTerm = params.get('utm_term') ?? undefined;

        //the internal referal info eg from global website
        const int_Source = params.get('zl_s') ?? undefined;
        const int_SourcePage = params.get('zl_sp') ?? undefined;
        const int_ComponentType = params.get('zl_ct') ?? undefined;
        const int_ComponentInternalName = params.get('zl_cin') ?? undefined;
        const int_ComponentTitle = params.get('zl_cti') ?? undefined;
        const int_ButtonText = params.get('zl_bt') ?? undefined;

        if (utmId || utmCampaign || utmContent || utmMedium || utmSource || utmTerm) {
            dispatch(AttributionActions.utmLoaded(utmId, utmCampaign, utmContent, utmMedium, utmSource, utmTerm));
        }

        if (
            int_Source ||
            int_SourcePage ||
            int_ComponentType ||
            int_ComponentInternalName ||
            int_ComponentTitle ||
            int_ButtonText
        ) {
            dispatch(
                AttributionActions.internalAttributionLoaded(
                    int_Source,
                    int_SourcePage,
                    int_ComponentType,
                    int_ComponentInternalName,
                    int_ComponentTitle,
                    int_ButtonText
                )
            );
        }
    }, [dispatch, location.search]);

    useEffect(() => {
        // Preview access code + venue ingress on every client-side navigation. The very first
        // load is seeded synchronously in loadState() instead - it has to be, because child
        // effects run before parent ones, so the tickets page's loadSites() would otherwise
        // request /sites before this effect could dispatch the code. This effect covers
        // subsequent navigations and mid-funnel refreshes.
        // Referenced as location.pathname / location.search rather than destructured so the
        // deps stay primitive - see the note on stable dependencies in the UTM effect above.
        const pathname = location.pathname;

        const accessCode = readAccessCodeFromLocation({ pathname, search: location.search });
        if (accessCode) dispatch(SiteActions.addAccessCode(accessCode));

        const funnelMatch =
            matchPath<{ venue?: string }>(pathname, { path: RoutePatterns.BookNow, exact: true }) ??
            matchPath<{ venue?: string }>(pathname, { path: RoutePatterns.PrivateEvent, exact: true });

        // The venue is dropped by the same clear-state path that drops the access code, and
        // nothing outside the tickets page reads it - so recover it here too, otherwise a
        // mid-funnel refresh still bounces the user out to the landing page.
        // NOTE this only sets preselectedSite. usePreselectSite() is what promotes it to
        // selectedSite, and it is mounted on the tickets page alone - so recovery completes
        // because Addons/Checkout/Complete all bounce back to book-now when the booking is
        // missing. CheckoutPtv never bounces and never loads the sites list, so the venue is
        // recorded here for onward navigation but is not restored on that page itself.
        const venue =
            funnelMatch?.params.venue ??
            matchPath<{ venue?: string }>(pathname, { path: RoutePatterns.Addons, exact: true })?.params
                .venue ??
            matchPath<{ venue?: string }>(pathname, { path: RoutePatterns.Checkout, exact: true })?.params
                .venue ??
            matchPath<{ venue?: string }>(pathname, { path: RoutePatterns.CheckoutPtv, exact: true })?.params
                .venue ??
            matchPath<{ venue?: string }>(pathname, { path: RoutePatterns.Complete, exact: true })?.params
                .venue;

        // Guarded on selectedSite because PRESELECT_SITE also flips selectedSiteUI
        if (venue && !selectedSite) dispatch(SiteActions.preselectSite(venue));
    }, [dispatch, location.pathname, location.search, selectedSite]);

    useEffect(() => {
        if (globalGtmEndpoint && globalGtmEndpoint !== '' && typeof globalGtmEndpoint != 'undefined') {
            GoogleTagManager.init(globalGtmEndpoint);
        }
    }, [globalGtmEndpoint]);

    useEffect(() => {
        const hubspotScript = document.createElement('script');

        hubspotScript.type = 'text/javascript';
        hubspotScript.id = 'hs-script-loader';
        hubspotScript.async = true;
        hubspotScript.defer = true;
        hubspotScript.src = `//js.hs-scripts.com/21452464.js`;

        document.head.appendChild(hubspotScript);

        return () => {
            document.head.removeChild(hubspotScript);
        };
    }, []);

    useEffect(() => {
        if (!appSettingsAreLoaded) {
            console.log('skipping gtm client key, app settings are not yet loaded');
            return;
        }

        if (
            typeof selectedSite == 'undefined' ||
            typeof selectedSiteGtmKey == 'undefined' ||
            selectedSiteGtmKey === ''
        ) {
            console.log('skipping gtm client key, selected site or client is not valid (site, key): (' +
                selectedSite +
                ', ' +
                selectedSiteGtmKey +
                ')'
            );
            return;
        }

        if (!gtmSendToClients) {
            console.log('Not running init for newly selected site ' +
                selectedSite?.name +
                '/' +
                selectedSite?.code +
                ' gtm endpoint ' +
                selectedSiteGtmKey +
                ' because gtmSendToClients is ' +
                gtmSendToClients
            );
            return;
        }

        if (gtmSendToClients) {
            const currentCookies = cookiesRef.current;
            addGtmContainer(
                selectedSite?.siteMetadata.googleTagManagerKey,
                selectedSiteGtmKey,
                {
                    ad_user_data: currentCookies[CookieNames.Marketing] ? "granted" : "denied",
                    ad_personalization: currentCookies[CookieNames.Marketing] ? "granted" : "denied",
                    ad_storage: currentCookies[CookieNames.Marketing] ? "granted" : "denied",
                    analytics_storage: currentCookies[CookieNames.Performance] ? "granted" : "denied",
                    personalization_storage: currentCookies[CookieNames.Functional] ? "granted" : "denied",
                    functionality_storage: "granted",
                    security_storage: "granted"
                }
            );
        }
    }, [selectedSiteGtmKey, appSettingsAreLoaded, gtmSendToClients, selectedSite]);

    useEffect(() => {
        if (logRocketId) {
            LogRocket.init(logRocketId);
        }
    }, [logRocketId]);

    useEffect(() => {
        if (bookingFailureMessage !== undefined) {
            const localisedMessage = t(bookingFailureMessage);
            message.error(localisedMessage);
        }
    }, [bookingFailureMessage, t]);

    useEffect(() => {
        window.scroll({
            top: 0,
            left: 0,
            behavior: 'smooth',
        });
    }, [location]);

    const configLocale = antdLocaleMap[currentLocale] ?? en_US;
  

    return (
        <React.Fragment>
            <HelmetProvider>
                <Helmet>
                    <link rel="canonical" href={`${window.location.origin}${location.pathname}`} />
                    {languageList.map((lang) => (
                        <link
                            key={lang.code}
                            rel="alternate"
                            hrefLang={lang.momentLocale}
                            href={`${window.location.origin}${replaceLanguageInPath(location.pathname, lang.momentLocale)}`}
                        />
                    ))}
                    <link rel="alternate" hrefLang="x-default" href={`${window.location.origin}${replaceLanguageInPath(location.pathname)}`} />
                    <title>{t('meta.pageTitle')}</title>
                    <meta name="description" content={t('meta.pageDescription')} />
                    <meta property="og:title" content={t('meta.pageTitle')} />
                    <meta property="og:description" content={t('meta.pageDescription')} />
                    <meta name="twitter:title" content={t('meta.pageTitle')} />
                    <meta name="twitter:description" content={t('meta.pageDescription')} />
                </Helmet>
                <AppInsightsInitializer />
                <AppInsightsContext.Provider value={appInsightsReactPlugin}>
                    {configCatSdkKey &&
                        <ErrorBoundry env={environment}>
                            <ConfigCatProvider sdkKey={configCatSdkKey}>
                                <FeatureFlagLoader />
                            </ConfigCatProvider>
                        </ErrorBoundry>
                    }

                    <GoogleTagManagerHeader />
                    
                    {showDebugPanel && <DebugPanel />}
                    <ErrorDisplayComponent />
                    <ExitIntentComponent />
                    <Content>
                        <Row className="booking-timer">
                            <ReservationTimerComponent onHeightChange={handleReservationTimerHeightChange} />
                        </Row>
                        <Row className="content-container">
                            <ConfigProvider direction={direction} locale={configLocale} csp={csp}>
                                <Switch>
                                    <Route
                                        exact path={RoutePatterns.GlobalGiftVoucherRedeem}
                                        render={({ match, location }) => (
                                            <Redirect
                                                to={`/${match.params.language || selectedLanguage.momentLocale}/${Routes.RegionalGiftVoucher}/${match.params.currency}/redeem-locations${location.search}`}
                                            />
                                        )}
                                    />

                                    <Route
                                        exact path={RoutePatterns.GlobalGiftVoucher}
                                        render={({ match, location }) => (
                                            <Redirect
                                                to={`/${match.params.language || selectedLanguage.momentLocale}/${Routes.RegionalGiftVoucher}${location.search}`}
                                            />
                                        )}
                                    />
                                    <Route exact path={RoutePatterns.RegionalGiftVoucherRedeem}>
                                        <RedeemLocationsPage />
                                    </Route>
                                    <Route exact path={RoutePatterns.RegionalGiftVoucher}>
                                        <GlobalGiftsPage />
                                    </Route>
                                    <Route exact path={RoutePatterns.BookNow}>
                                        <TicketsPage reservationTimerHeight={reservationTimerHeight} />
                                    </Route>
                                    <Route exact path={RoutePatterns.GiftVoucher}>
                                        <TicketsPage reservationTimerHeight={reservationTimerHeight} />
                                    </Route>
                                    <Route exact path={RoutePatterns.PrivateEvent}>
                                        <TicketsPage reservationTimerHeight={reservationTimerHeight} />
                                    </Route>
                                    <Route exact path={RoutePatterns.Addons}>
                                        <AddOnsComponent />
                                    </Route>
                                    <Route exact path={RoutePatterns.Checkout}>
                                        <CheckoutComponent />
                                    </Route>
                                    <Route exact path={RoutePatterns.CheckoutPtv}>
                                        <CheckoutPtvComponent />
                                    </Route>
                                    <Route exact path={RoutePatterns.Complete}>
                                        <CompleteComponent />
                                    </Route>
                                    <Route exact path={RoutePatterns.Booking}>
                                        <DetailsComponent />
                                    </Route>
                                    <Route exact path={RoutePatterns.SplitPayment}>
                                        <SplitPaymentComponent />
                                    </Route>
                                    <Route exact path={RoutePatterns.SplitAllocate}>
                                        <SplitAllocateComponent />
                                    </Route>
                                    <Route exact path={RoutePatterns.SplitJoin}>
                                        <SplitJoinComponent />
                                    </Route>
                                    <Route exact path={RoutePatterns.BalancePayment}>
                                        <BalancePaymentComponent />
                                    </Route>
                                    <Route path={RoutePatterns.Processing}>
                                        <ProcessingPage />
                                    </Route>
                                    <Route exact path={RoutePatterns.Root}>
                                        <TicketsPage reservationTimerHeight={reservationTimerHeight} />
                                    </Route>
                                    <Route path="*">
                                        <NotFoundComponent />
                                    </Route>
                                </Switch>
                            </ConfigProvider>
                        </Row>
                    </Content>

                    <Cookie />

                </AppInsightsContext.Provider>
            </HelmetProvider>
        </React.Fragment>
    );
};

export default App;
